import { useState, useEffect, useCallback, useMemo } from 'react';
import { DocumentIdentityService,
// DocumentIdentityEvent, // Correctly imported from @readsavor/types now
// DocumentIdentityEventType,
 } from '../services/DocumentIdentityService.js';
import { useDocumentState } from '../state/DocumentState.js';
export function useCurrentDocumentLoader() {
    const { documentState, loadDocument: performLoadDocument } = useDocumentState();
    const [identityServiceInstance] = useState(() => DocumentIdentityService.getInstance());
    const [targetIdentity, setTargetIdentity] = useState(identityServiceInstance.state.currentIdentity);
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState(null);
    useEffect(() => {
        const handleIdentityChange = () => {
            const newTargetIdentity = identityServiceInstance.state.currentIdentity;
            setTargetIdentity(newTargetIdentity);
        };
        const unsubscribe = identityServiceInstance.addIdentityChangeListener(handleIdentityChange);
        setTargetIdentity(identityServiceInstance.state.currentIdentity);
        return () => unsubscribe();
    }, [identityServiceInstance]);
    const triggerLoad = useCallback(async (identityToLoad) => {
        if (!identityToLoad) {
            setIsLoading(false);
            setError(null);
            // If identityToLoad is null, and documentState reflects something loaded,
            // we might need to explicitly reset documentState via a call from useDocumentState if not already handled.
            // For now, just ensuring isLoading and error are reset.
            return;
        }
        // Prevent re-triggering if already loaded and no error
        if (documentState.identity?.id === identityToLoad.id &&
            documentState.identity?.type === identityToLoad.type &&
            documentState.contentType === identityToLoad.type &&
            !error // Ensure no error state for this currently loaded document
        ) {
            if (isLoading)
                setIsLoading(false); // Should ideally be false if correctly loaded
            // setError(null); // No need to clear error if it's already null from previous successful load
            return;
        }
        setIsLoading(true);
        setError(null); // Clear previous errors before a new load attempt
        try {
            // performLoadDocument is expected to update documentState internally via setDocumentState in useDocumentState
            await performLoadDocument(identityToLoad);
            // If performLoadDocument resolves successfully, documentState should update.
            // The second useEffect will then handle setting isLoading to false.
        }
        catch (e) {
            console.error('[USE_CURRENT_DOCUMENT_LOADER] Error during performLoadDocument for identity.', {
                identityToLoad,
                error: e instanceof Error ? e.message : String(e),
            });
            if (e instanceof Error) {
                setError(e);
            }
            else {
                setError(new Error('An unknown error occurred while loading the document'));
            }
            setIsLoading(false); // Set isLoading to false on error
        }
    }, 
    // Corrected dependencies for triggerLoad:
    // It depends on performLoadDocument and the parts of documentState used for comparison.
    // isLoading and error are managed by this hook and should not be dependencies here.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
        performLoadDocument,
        documentState.identity?.id,
        documentState.identity?.type,
        documentState.contentType,
        // documentState.error, // Not needed if we use the local `error` for the check
        // We are checking local `error` state in the condition, so not needed in deps for `triggerLoad` itself.
    ]);
    useEffect(() => {
        // Cast documentState to any to access .error and .identity if the type isn't fully propagated yet
        const dsIdentity = documentState.identity;
        const dsError = documentState.contentType === undefined ? documentState.error : undefined;
        let shouldAttemptLoad = false;
        if (targetIdentity) {
            const isDifferentFromActive = documentState.contentType !== targetIdentity.type ||
                dsIdentity?.id !== targetIdentity.id || // Compare with dsIdentity from documentState
                dsIdentity?.type !== targetIdentity.type;
            let isErrorStateForCurrentTarget = false;
            if (documentState.contentType === undefined && dsError) {
                if (dsIdentity?.id === targetIdentity.id && dsIdentity?.type === targetIdentity.type) {
                    isErrorStateForCurrentTarget = true;
                }
            }
            if (isDifferentFromActive && !isErrorStateForCurrentTarget) {
                shouldAttemptLoad = true;
            }
        }
        const needsToClear = !targetIdentity && documentState.contentType !== undefined;
        if (shouldAttemptLoad) {
            if (!isLoading) {
                triggerLoad(targetIdentity);
            }
            else {
                // console.log(`[DEBUG_LOADER_EFFECT] Would load target ${currentTargetIdStr}, but isLoading is true.`);
            }
        }
        else if (needsToClear) {
            if (!isLoading) {
                // console.log(`[DEBUG_LOADER_EFFECT] Calling triggerLoad(null) to clear document.`);
                triggerLoad(null);
            }
            else {
                // console.log(`[DEBUG_LOADER_EFFECT] Would clear document, but isLoading is true.`);
            }
        }
        else {
            // console.log(
            //   `[DEBUG_LOADER_EFFECT] No load/clear action needed. Target: ${currentTargetIdStr}, isErrorStateForCurrentTarget: ${isErrorStateForCurrentTarget}, isDifferentFromActive: ${isDifferentFromActive}`
            // );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        targetIdentity,
        documentState.identity,
        documentState.contentType,
        triggerLoad,
        isLoading,
        error,
    ]); // `error` is local error
    useEffect(() => {
        if (isLoading) {
            if (targetIdentity &&
                documentState.identity?.id === targetIdentity.id &&
                documentState.identity?.type === targetIdentity.type &&
                documentState.contentType === targetIdentity.type) {
                setIsLoading(false);
                setError(null);
            }
            else if (!targetIdentity && !documentState.contentType) {
                setIsLoading(false);
                setError(null);
            }
        }
    }, [documentState.identity, documentState.contentType, targetIdentity, isLoading]);
    const reload = useCallback(() => {
        if (targetIdentity) {
            setIsLoading(true); // Indicate loading immediately on manual reload
            setError(null);
            performLoadDocument(targetIdentity).catch(e => {
                console.error('[USE_CURRENT_DOCUMENT_LOADER] Error during manual reload.', {
                    targetIdentity,
                    error: e,
                });
                setError(e instanceof Error ? e : new Error('Failed to reload document'));
                setIsLoading(false);
            });
        }
        else {
            /* empty */
        }
    }, [targetIdentity, performLoadDocument]);
    // Temporary debugging useEffect to observe the document state
    useEffect(() => {
        if (!isLoading && documentState.contentType === 'text') {
            // console.debug('[USE_CURRENT_DOCUMENT_LOADER] Paragraphs field directly accessed:', (documentState as any).paragraphs);
        }
        else if (!isLoading && documentState.contentType === 'pdf') {
            /* empty */
        }
        else if (!isLoading && documentState.contentType === undefined) {
            /* empty */
        }
    }, [documentState, isLoading]); // Depend on documentState from useDocumentState and the local isLoading
    return useMemo(() => ({
        document: documentState,
        isLoading,
        error,
        targetIdentity,
        reload,
    }), [documentState, isLoading, error, targetIdentity, reload]);
}
//# sourceMappingURL=useCurrentDocumentLoader.js.map